import { Component } from '@theme/component';

/**
 * `<bundle-personalization>` — per-step personalization (embroidery / monogram).
 *
 * Owns the "Add Personalization" add-on row for one bundle step. The toggle
 * binds to `bundle-builder/togglePersonalization?step=N`; each field binds to
 * `bundle-builder/setPersonalizationField?step=N` with a `data-field` attribute
 * (the field name becomes a cart line-item property).
 *
 * The offer belongs to the SELECTED PRODUCT — every bag carries its own
 * `custom.embroidery_config`, exactly like a standard PDP — so on every
 * `bundle:update` this component re-reads it from the builder
 * (`getPersonalization`) and swaps the fee badge, preview, fields and footnote
 * to match. A bag that can't be embroidered resolves to `null` and the whole row
 * hides. It then reflects the on/off state (revealing or hiding the field group)
 * and keeps every text field's live character counter in sync with the value
 * held on the builder (`state.personalizationProps`).
 *
 * Contract owned: `data-step`, `ref="fields"`, `ref="check"` (the toggle),
 * `ref="count[]"` (per-field counters), `data-field` on every input/select, and
 * the swapped regions `ref="fee" | "preview" | "previewImg" | "fieldList" |
 * "footnote"`.
 */
export class BundlePersonalizationComponent extends Component {
  #builder = null;
  #onUpdate = null;

  /**
   * Thread colours offered when neither the product's embroidery config nor the
   * step's personalization metaobject lists any. Mirrors the same fallback in
   * snippets/bb-personalization.liquid so a colour field always renders a
   * usable <select>, server-rendered or re-rendered.
   */
  static FALLBACK_THREADS = ['White', 'Black', 'Navy', 'Toasted Almond', 'Forest Green', 'Gold'];

  connectedCallback() {
    super.connectedCallback();
    this.stepIndex = Number(this.dataset.step);
    this.#builder = this.closest('bundle-builder');
    this.#onUpdate = this.#handleUpdate.bind(this);
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    // Wire counters once up-front so they reflect any default/typed value.
    this.#syncCounters({});
    // The row's contents depend on which bag is selected, so pull current state
    // rather than waiting for the next change — this component may upgrade after
    // the builder's initial broadcast.
    this.#builder?.requestUpdate?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#onUpdate) this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  #handleUpdate() {
    const builder = this.#builder;
    if (!builder) return;
    // Read the DRAFT — personalization is edited in-step and commits on Save.
    const sel = builder.getDraftSelection(this.stepIndex);
    if (!sel) return;

    // Resolved from the selected bag (step-level metaobject as the fallback).
    // Never `?? sel.step.personalization` here: a deliberate null — the bag
    // can't be embroidered — must not be papered over by the fallback the
    // resolver already considered and rejected.
    const pers = builder.getPersonalization(this.stepIndex, sel.product);

    // Nothing to offer for this bag: hide the row entirely. The builder has
    // already forced `personalizationOn` off, so the checkbox must follow —
    // otherwise it would still read as checked if the shopper switches back.
    this.toggleAttribute('hidden', !pers);
    if (!pers) {
      if (this.refs.check instanceof HTMLInputElement) this.refs.check.checked = false;
      return;
    }

    const props = sel.state.personalizationProps || {};
    this.#renderOffer(pers, props, builder);

    const on = !!sel.state.personalizationOn;
    if (this.refs.check instanceof HTMLInputElement) this.refs.check.checked = on;
    if (this.refs.fields) this.refs.fields.toggleAttribute('hidden', !on);
    this.toggleAttribute('data-active', on);
    this.#syncCounters(props);
  }

  /** Signature of the offer currently in the DOM, so typing doesn't re-render. */
  #renderKey = null;

  /**
   * Swap the row's contents to a different product's embroidery offer. Skipped
   * unless the offer actually changed — `bundle:update` fires on every
   * keystroke, and rebuilding the inputs mid-word would lose the caret.
   * @param {any} pers
   * @param {Record<string,string>} props
   * @param {any} builder
   */
  #renderOffer(pers, props, builder) {
    const key = JSON.stringify([
      pers.feeVariantId ?? null,
      pers.feePrice ?? 0,
      pers.maxChars ?? null,
      pers.fields ?? [],
      pers.image ?? '',
      pers.description ?? '',
      pers.threadColors ?? [],
    ]);
    if (key === this.#renderKey) return;
    this.#renderKey = key;

    const cents = Number(pers.feePrice) || 0;
    if (this.refs.fee instanceof HTMLElement) {
      // `> 0`, not truthiness: a bag whose config sets `free_embroidery` comes
      // through with `feePrice: 0`, which must read "Free" rather than "+$0".
      // Reaching here at all means an offer exists (`pers` is non-null), so the
      // pill always says something. Twin of the same branch in
      // snippets/bb-personalization.liquid: this runs on every bundle:update, so
      // if the two disagreed the badge would flip the moment a swatch is picked.
      this.refs.fee.textContent = cents > 0 ? `+${builder.money(cents).replace('.00', '')}` : 'Free';
    }

    if (this.refs.preview instanceof HTMLElement) this.refs.preview.toggleAttribute('hidden', !pers.image);
    if (this.refs.previewImg instanceof HTMLImageElement && pers.image) {
      this.refs.previewImg.src = pers.image;
      this.refs.previewImg.alt = pers.label || 'Personalization preview';
    }

    if (this.refs.footnote instanceof HTMLElement) {
      const note = String(pers.description ?? '');
      this.refs.footnote.innerHTML = note;
      this.refs.footnote.toggleAttribute('hidden', !note.trim());
    }

    this.#renderFields(pers, props);
  }

  /**
   * Rebuild the field list for the resolved offer (label, maxlength, counter,
   * thread options). `on:` bindings are delegated from the document and read at
   * event time, so re-created controls stay wired; `updatedCallback()` refreshes
   * `refs` immediately rather than waiting on the mutation observer.
   * @param {any} pers
   * @param {Record<string,string>} props
   */
  #renderFields(pers, props) {
    const list = this.refs.fieldList;
    if (!(list instanceof HTMLElement)) return;

    const fields = Array.isArray(pers.fields) ? pers.fields : [];
    const max = Number(pers.maxChars) || 10;
    const step = this.stepIndex;
    const threads = this.#threadOptions(pers);

    list.innerHTML = fields
      .map((name, index) => {
        const id = `bb-pers-${step}-${index}`;
        const field = this.#esc(name);
        const isColor = /colou?r|thread/i.test(name);
        const control = isColor
          ? `<select id="${id}" class="bb-personalization__select" data-field="${field}"` +
            ` on:change="bundle-builder/setPersonalizationField?step=${step}">${threads}</select>`
          : `<div class="bb-personalization__input-wrap">` +
            `<input type="text" id="${id}" class="bb-personalization__input" maxlength="${max}"` +
            ` data-field="${field}" data-max="${max}"` +
            ` on:input="bundle-builder/setPersonalizationField?step=${step}">` +
            `<span class="bb-personalization__count" ref="count[]" data-field="${field}">0/${max}</span>` +
            `</div>`;
        return (
          `<div class="bb-personalization__field">` +
          `<label class="bb-personalization__field-label" for="${id}">${field} (Max ${max} Characters)</label>` +
          `${control}</div>`
        );
      })
      .join('');

    this.updatedCallback();

    // Field names are stable across products, so text the shopper already typed
    // survives the swap — put it back into the freshly-built inputs, clamped to
    // the new bag's limit (`maxlength` only constrains typing, not assignment).
    for (const control of this.querySelectorAll('input[data-field], select[data-field]')) {
      const input = /** @type {HTMLInputElement|HTMLSelectElement} */ (control);
      const value = props[input.dataset.field ?? ''];
      if (value == null) continue;
      input.value = input instanceof HTMLInputElement ? value.slice(0, max) : value;
    }
  }

  /** `<option>` markup for a thread-colour field. @param {any} pers */
  #threadOptions(pers) {
    const colors = Array.isArray(pers.threadColors) && pers.threadColors.length ? pers.threadColors : null;
    const list = colors
      ? colors.map((c) => ({ label: c.label ?? c.value, value: c.value ?? c.label }))
      : BundlePersonalizationComponent.FALLBACK_THREADS.map((label) => ({ label, value: label }));
    return list.map((c) => `<option value="${this.#esc(c.value)}">${this.#esc(c.label)}</option>`).join('');
  }

  /** @param {string} value */
  #esc(value) {
    return String(value ?? '').replace(
      /[&<>"]/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
    );
  }

  /**
   * Update each text field's "n/max" counter from current state.
   * Selects (thread color) have no counter, so they are skipped naturally.
   * @param {Record<string,string>} props
   */
  #syncCounters(props) {
    const counters = this.#refArray('count');
    for (const counter of counters) {
      const field = counter.dataset.field;
      const input = this.querySelector(`input[data-field="${CSS.escape(field)}"]`);
      const max = Number(input?.dataset.max || counter.textContent.split('/')[1] || 0);
      // Clamped: state can outlive a switch to a bag with a shorter limit, and
      // the counter must agree with the (clamped) input and the cart property.
      const value = String(props[field] ?? input?.value ?? '');
      const len = max > 0 ? Math.min(value.length, max) : value.length;
      counter.textContent = `${len}/${max}`;
    }
  }

  /** `ref="x[]"` resolves to an array; normalize single/array/undefined. */
  #refArray(name) {
    const ref = this.refs[name];
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }
}

if (!customElements.get('bundle-personalization')) {
  customElements.define('bundle-personalization', BundlePersonalizationComponent);
}
